// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import { ExtendedCoilModel } from 'src/app/_interfaces/coil/coil-extension-model';
import { ApiMarkerModel } from 'src/app/api/remote-api/.coil.generated/models/api-marker-model';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { CoilService } from '../../services/coil.service';
import { CoilHistoryComponent } from '../coil-history/coil-history.component';
import { CoilInformationComponent } from '../coil-information/coil-information.component';

@Component({
  selector: 'app-coil-detail',
  templateUrl: './coil-detail.component.html',
  styleUrls: ['./coil-detail.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CoilHistoryComponent,
    CoilInformationComponent,
    CommonModule,
    TranslateModule,
  ],
})
export class CoilDetailComponent implements OnChanges {
  @Input({ required: true })
  coil!: ExtendedCoilModel;
  @Input()
  marker?: Observable<ApiMarkerModel | null>;
  @Input({ required: true })
  currentlyExpandedCoil!: string | null;
  @Input({ required: true })
  toggleCoilExpansion!: (coilSerialNumber: string) => void;
  expandedCoil = false;

  @ViewChild(CoilHistoryComponent) coilHistoryComponent!: CoilHistoryComponent;
  @ViewChildren('coilName') currentCoilName!: QueryList<ElementRef>;

  channelToggleLabel = 'Show Channels';

  constructor(
    private coilService: CoilService,
    public screenLayoutService: ScreenLayoutService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['coil']) {
      this.updateMarker();
    }
  }

  updateMarker(): void {
    if (this.isCoilMarkersInitialized()) {
      this.marker = this.getCoilMarker();
    } else {
      console.error('getCoilMarkers or lastSuccessfulData$ is not initialized');
    }
  }

  isCoilMarkersInitialized(): boolean {
    return (
      !!this.coilService.getCoilMarkers &&
      !!this.coilService.getCoilMarkers.lastSuccessfulData$
    );
  }

  getCoilMarker(): Observable<ApiMarkerModel | null> {
    return this.coilService.getCoilMarkers.lastSuccessfulData$.pipe(
      map(
        (markers) =>
          markers.find((m) => m.coilOkStatus === this.coil.coilStatus) ?? null
      )
    ) as Observable<ApiMarkerModel | null>;
  }

  changeArrowIcons(): string {
    return this.currentlyExpandedCoil === this.coil.coilSerialNumber
      ? 'arrow-line-up-s'
      : 'arrow-line-down-s';
  }

  scrollToCoilName(): void {
    requestAnimationFrame(() => {
      const currentCoilName = this.currentCoilName.last;
      currentCoilName.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }
}
